import { useState } from 'react';
import { CONTACT_INFO } from '../data';

export default function Contact() {
  const [formData, setFormData] = useState({
    Navn: '',
    Telefonnummer: '',
    'E-mail': '',
    Beskrivelse: '',
    website_url: '' // Honeypot field
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Der opstod en fejl ved afsendelse af formularen. Prøv venligst igen.');
      }

      const result = await response.json();
      
      if (result.status === 'ok') {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          Navn: '',
          Telefonnummer: '',
          'E-mail': '',
          Beskrivelse: '',
          website_url: ''
        });
      } else {
        throw new Error('Formularen blev afvist af serveren.');
      }
    } catch (err) {
      console.error('Fejl ved indsendelse af formular:', err);
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der opstod en uventet fejl. Prøv igen senere.'
      });
    }
  };

  return (
    <div className="w-full">
      {/* Page Banner */}
      <section className="relative h-[40vh] min-h-[250px] w-full overflow-hidden bg-primary-green">
        <img 
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/340cd821-77c4-4f44-a38b-9945cfcac15b/vedligeholdelse-garden.jpg" 
          alt="Kontakt os" 
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-green via-primary-green/50 to-transparent" />
        {/* Decorative Background Leaves */}
        <div className="absolute inset-0 pointer-events-none opacity-10 flex justify-between items-center px-12 overflow-hidden z-10">
          <i className="fa-solid fa-leaf text-white text-5xl animate-float-leaf-1 transform translate-y-12" />
          <i className="fa-solid fa-leaf text-white text-4xl animate-float-leaf-2 transform -translate-y-12 hidden md:block" />
          <i className="fa-solid fa-leaf text-white text-6xl animate-float-leaf-3 transform translate-y-6 hidden lg:block" />
        </div>
        <div className="relative z-20 mx-auto flex h-full max-w-7xl flex-col justify-end px-4 pb-12 sm:px-6 lg:px-8 text-white">
          <span className="text-xs font-bold uppercase tracking-widest text-secondary-green block mb-2">
            Få et uforpligtende tilbud
          </span>
          <h1 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl uppercase">
            Kontakt os
          </h1>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 md:py-24 bg-white relative overflow-hidden">
        {/* Decorative Background Leaves */}
        <div className="absolute inset-0 pointer-events-none opacity-5 flex justify-between items-center px-12 overflow-hidden">
          <i className="fa-solid fa-leaf text-primary-green text-5xl animate-float-leaf-1 transform translate-y-12" />
          <i className="fa-solid fa-leaf text-primary-green text-4xl animate-float-leaf-2 transform -translate-y-12 hidden md:block" />
          <i className="fa-solid fa-leaf text-primary-green text-6xl animate-float-leaf-3 transform translate-y-6 hidden lg:block" />
        </div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Column: Contact Details */}
            <div className="space-y-8">
              <div className="space-y-4">
                <span className="text-xs font-bold uppercase tracking-widest text-secondary-green block">
                  Find os her
                </span>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-primary-green">
                  Gør noget godt for din have
                </h2>
                <p className="font-body text-sm text-neutral-muted leading-relaxed">
                  Udfyld formularen til højre, så vender vi tilbage med et uforpligtende tilbud hurtigst muligt. Du er også altid velkommen til at ringe eller sende en e-mail.
                </p>
              </div>

              {/* Contact Info Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Phone */}
                <div className="bg-neutral-light-bg p-6 rounded-sm border border-gray-100 flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-secondary-green/10 flex items-center justify-center text-secondary-green text-lg shrink-0">
                    <i className="fa-solid fa-phone" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-display text-xs font-bold uppercase tracking-wider text-primary-green">Telefon</h4>
                    <a 
                      href={`tel:${CONTACT_INFO.phoneRaw}`} 
                      className="text-xs text-neutral-muted hover:text-primary-green transition-colors whitespace-nowrap block"
                    >
                      <span className="whitespace-nowrap">{CONTACT_INFO.phone}</span>
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="bg-neutral-light-bg p-6 rounded-sm border border-gray-100 flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-secondary-green/10 flex items-center justify-center text-secondary-green text-lg shrink-0">
                    <i className="fa-solid fa-envelope" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-display text-xs font-bold uppercase tracking-wider text-primary-green">E-mail</h4>
                    <a 
                      href={`mailto:${CONTACT_INFO.email}`} 
                      className="text-xs text-neutral-muted hover:text-primary-green transition-colors whitespace-nowrap block"
                    >
                      <span className="whitespace-nowrap">{CONTACT_INFO.email}</span>
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="bg-neutral-light-bg p-6 rounded-sm border border-gray-100 flex gap-4 sm:col-span-2">
                  <div className="w-10 h-10 rounded-full bg-secondary-green/10 flex items-center justify-center text-secondary-green text-lg shrink-0">
                    <i className="fa-solid fa-location-dot" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-display text-xs font-bold uppercase tracking-wider text-primary-green">Adresse</h4>
                    <p className="text-xs text-neutral-muted">{CONTACT_INFO.address}</p>
                    <a 
                      href="https://maps.google.com/?q=Vestervænget+10,+6710+Esbjerg+V" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[10px] font-bold text-secondary-green hover:text-primary-green transition-colors uppercase tracking-wider inline-block pt-1"
                    >
                      Klik her for rutevejledning
                    </a>
                  </div>
                </div>
              </div>

              {/* Telefontider Box */}
              <div className="bg-primary-green text-white p-6 rounded-sm space-y-4 shadow-sm">
                <h3 className="font-display text-sm font-bold uppercase tracking-wider text-secondary-green flex items-center gap-2">
                  <i className="fa-solid fa-clock" />
                  Telefontider
                </h3>
                <div className="space-y-2 text-xs text-gray-200">
                  <p className="flex justify-between">
                    <span>Mandag - fredag:</span>
                    <span className="font-semibold">08.00 - 16.00</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Lørdag og søndag:</span>
                    <span className="font-semibold">Lukket</span>
                  </p>
                  <p className="text-secondary-green italic pt-2 border-t border-primary-green-light/30 text-[11px]">
                    {CONTACT_INFO.hoursMails}
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="bg-neutral-light-bg p-8 rounded-sm border border-gray-100 shadow-xs">
              <h3 className="font-display text-lg font-bold uppercase tracking-wider text-primary-green mb-6 border-b border-gray-200 pb-3">
                Send os en besked
              </h3>

              {status.success ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-6 text-center space-y-4 animate-in fade-in duration-200">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xl mx-auto">
                    <i className="fa-solid fa-circle-check" />
                  </div>
                  <h4 className="font-display text-sm font-bold text-emerald-800 uppercase tracking-wider">
                    Tak for din henvendelse!
                  </h4>
                  <p className="text-xs text-emerald-700 leading-relaxed">
                    Vi har modtaget din besked og vender tilbage til dig med et uforpligtende tilbud hurtigst muligt.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Antispam Honeypot Field (ABSOLUTELY HIDDEN from humans) */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <label htmlFor="website_url">Udfyld ikke dette felt hvis du er et menneske</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  {/* Name Field */}
                  <div className="space-y-1.5">
                    <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-primary-green">
                      Navn <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="Navn"
                      required
                      value={formData.Navn}
                      onChange={handleChange}
                      className="w-full bg-white border border-gray-200 rounded-sm px-4 py-3 text-xs text-neutral-dark focus:outline-none focus:border-secondary-green transition-colors"
                      placeholder="Dit fulde navn"
                    />
                  </div>

                  {/* Phone Field */}
                  <div className="space-y-1.5">
                    <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-primary-green">
                      Telefonnummer <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="Telefonnummer"
                      required
                      value={formData.Telefonnummer}
                      onChange={handleChange}
                      className="w-full bg-white border border-gray-200 rounded-sm px-4 py-3 text-xs text-neutral-dark focus:outline-none focus:border-secondary-green transition-colors"
                      placeholder="Dit telefonnummer"
                    />
                  </div>

                  {/* Email Field */}
                  <div className="space-y-1.5">
                    <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-primary-green">
                      E-mail <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="E-mail"
                      required
                      value={formData['E-mail']}
                      onChange={handleChange}
                      className="w-full bg-white border border-gray-200 rounded-sm px-4 py-3 text-xs text-neutral-dark focus:outline-none focus:border-secondary-green transition-colors"
                      placeholder="Din e-mailadresse"
                    />
                  </div>

                  {/* Description Field */}
                  <div className="space-y-1.5">
                    <label htmlFor="description" className="block text-xs font-bold uppercase tracking-wider text-primary-green">
                      Beskrivelse <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="description"
                      name="Beskrivelse"
                      required
                      rows="5"
                      value={formData.Beskrivelse}
                      onChange={handleChange}
                      className="w-full bg-white border border-gray-200 rounded-sm px-4 py-3 text-xs text-neutral-dark focus:outline-none focus:border-secondary-green transition-colors resize-none"
                      placeholder="Beskriv din opgave her (f.eks. hækklipning, belægning, havepleje...)"
                    />
                  </div>

                  {/* Error Message */}
                  {status.error && (
                    <div className="bg-red-50 border border-red-200 rounded-sm p-4 text-xs text-red-700 flex items-start gap-2.5">
                      <i className="fa-solid fa-circle-exclamation mt-0.5 shrink-0" />
                      <span>{status.error}</span>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={status.submitting}
                    className="w-full bg-accent-orange hover:bg-accent-orange-dark disabled:bg-gray-400 text-white font-display text-xs font-bold py-4 uppercase tracking-widest transition-all duration-200 rounded-sm shadow-sm hover:scale-[1.01] cursor-pointer flex items-center justify-center gap-2"
                  >
                    {status.submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Sender...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane" />
                        Send besked
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
